import gsap from "gsap";
import { useEffect } from "react";

import classes from "./MouseTrail.module.scss";

const SVGNS = "http://www.w3.org/2000/svg";
export const COLORS = [
  "var(--750-bright-red)",
  "var(--750-sunny-orange)",
  "var(--750-light-green)",
  "var(--750-deep-blue)",
  "var(--750-purple)",
  "var(--750-bright-red)",
];

gsap.ticker.fps(60);

const useMouseTrail = (targetElement) => {
  useEffect(() => {
    const trailManager = {
      pointer: { x: window.innerWidth / 2, y: window.innerHeight / 2 },
      trailPoints: [],
      isPaused: false,
      isInsideHero: false,
      maxTrailLength: 15,
      svg: null,
      trailLine: null,
      customCursor: null,
      gradient: null,
      targetElement,

      isEnabled() {
        return !!this.targetElement;
      },

      init() {
        if (!this.isEnabled()) {
          return;
        }

        this.createCustomCursor();
        this.createTrail();
        this.addEventListeners();
        this.startAnimation();
      },

      createCustomCursor() {
        this.customCursor = document.createElement("div");
        this.customCursor.classList.add(classes.custom_cursor);
        this.targetElement.appendChild(this.customCursor);
      },

      createTrail() {
        this.svg = document.createElementNS(SVGNS, "svg");
        this.svg.classList.add(classes.rainbow_trail);

        this.trailLine = this.createTrailLine();
        this.gradient = this.createGradient(COLORS);

        const defs = document.createElementNS(SVGNS, "defs");
        defs.appendChild(this.gradient);
        this.svg.appendChild(defs);

        this.trailLine.setAttribute("stroke", "url(#animatedRainbowGradient)");
        this.svg.appendChild(this.trailLine);
        this.targetElement.appendChild(this.svg);

        gsap.to(`#animatedRainbowGradient`, {
          attr: { x1: "100%", x2: "200%" },
          duration: 3,
          repeat: -1,
          ease: "linear",
        });
      },

      createTrailLine() {
        const line = document.createElementNS(SVGNS, "path");
        line.classList.add(classes.trail_line);
        return line;
      },

      createGradient(colors) {
        const gradient = document.createElementNS(SVGNS, "linearGradient");
        gradient.id = "animatedRainbowGradient";
        gradient.setAttribute("x1", "0%");
        gradient.setAttribute("y1", "0%");
        gradient.setAttribute("x2", "100%");
        gradient.setAttribute("y2", "0%");

        colors.forEach((color, index) => {
          const stop = document.createElementNS(SVGNS, "stop");
          stop.setAttribute(
            "offset",
            `${(index / (colors.length - 1)) * 100}%`,
          );
          stop.setAttribute("stop-color", color);
          gradient.appendChild(stop);
        });

        return gradient;
      },

      updatePointer(event) {
        const elementAtPointer = document.elementFromPoint(
          event.clientX,
          event.clientY,
        );

        if (this.isEnabled() && this.targetElement.contains(elementAtPointer)) {
          this.pointer = { x: event.clientX, y: event.clientY };
          this.isInsideHero = true;
          this.customCursor.style.opacity = "1";
          document.body.classList.add(classes.hide_default_cursor);
        } else {
          this.isInsideHero = false;
          this.customCursor.style.opacity = "0";
          document.body.classList.remove(classes.hide_default_cursor);
        }
      },

      isPointerInsideRect(event, rect) {
        return (
          event.clientX >= rect.left &&
          event.clientX <= rect.right &&
          event.clientY >= rect.top &&
          event.clientY <= rect.bottom
        );
      },

      updateTrailPath() {
        if (!this.isEnabled() || this.isPaused) {
          return;
        }

        if (this.isInsideHero) {
          this.trailPoints.push({ x: this.pointer.x, y: this.pointer.y });
          if (this.trailPoints.length > this.maxTrailLength) {
            this.trailPoints.shift();
          }

          gsap.set(this.customCursor, { x: this.pointer.x, y: this.pointer.y });
        } else if (this.trailPoints.length > 0) {
          this.trailPoints.shift();
        }

        const pathData = this.generatePathData(this.trailPoints);
        this.trailLine.setAttribute("d", pathData);

        if (this.trailPoints.length >= 2) {
          const angle = this.calculateAngle(
            this.trailPoints.at(-2),
            this.trailPoints.at(-1),
          );
          this.updateGradientDirection(angle);
        }

        requestAnimationFrame(this.updateTrailPath.bind(this));
      },

      calculateAngle(pointA, pointB) {
        return Math.atan2(pointB.y - pointA.y, pointB.x - pointA.x);
      },

      updateGradientDirection(angle) {
        const [x1, y1, x2, y2] = this.calculateGradientPoints(angle);
        gsap.to(this.gradient, {
          attr: { x1: `${x1}%`, y1: `${y1}%`, x2: `${x2}%`, y2: `${y2}%` },
          duration: 0.2,
          ease: "power2.out",
        });
      },

      calculateGradientPoints(angle) {
        return [
          50 - Math.cos(angle) * 50,
          50 - Math.sin(angle) * 50,
          50 + Math.cos(angle) * 50,
          50 + Math.sin(angle) * 50,
        ];
      },

      generatePathData(points) {
        return points
          .map((point, index) =>
            index === 0 ? `M ${point.x} ${point.y}` : `L ${point.x} ${point.y}`,
          )
          .join(" ");
      },

      addEventListeners() {
        window.addEventListener("pointermove", this.updatePointer.bind(this));
        window.addEventListener("blur", this.handleWindowBlur.bind(this));
        window.addEventListener("focus", this.handleWindowFocus.bind(this));
      },

      removeEventListeners() {
        window.removeEventListener(
          "pointermove",
          this.updatePointer.bind(this),
        );
        window.removeEventListener("blur", this.handleWindowBlur.bind(this));
        window.removeEventListener("focus", this.handleWindowFocus.bind(this));
      },

      handleWindowBlur() {
        this.isPaused = true;
      },

      handleWindowFocus() {
        this.isPaused = false;
        this.startAnimation();
      },

      startAnimation() {
        if (!this.isEnabled()) {
          return;
        }
        requestAnimationFrame(this.updateTrailPath.bind(this));
      },

      cleanup() {
        this.removeEventListeners();
        this.svg?.remove();
        this.customCursor?.remove();
        document.body.classList.remove(classes.hide_default_cursor);
      },
    };

    trailManager.init();

    return () => {
      trailManager.cleanup();
    };
  }, [targetElement]);
};

export default useMouseTrail;
